import {
  Box,
  Divider,
  Flex,
  FlexProps,
  FormControl,
  FormControlProps,
  FormErrorMessage,
  FormLabel,
  FormLabelProps,
  Icon,
  IconButton,
  Input,
  List,
  ListItem,
  Text,
  VisuallyHiddenInput,
} from "@chakra-ui/react";
import { useCombobox } from "downshift";
import React, { Fragment, useMemo, useState } from "react";
import {
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiCloseLine,
  RiSearchLine,
} from "react-icons/ri";
import { ConditionalTooltip } from "src/components/Layout/ConditionalTooltip";
import { HighlightedText } from "src/components/Text/HighlightedText";
import {
  filterGroups,
  toGroupList,
  toItemList,
  type Group,
  type Item as SearchableItem,
} from "src/types/groupedItems";

export type Item = SearchableItem & {
  description?: React.ReactNode;
  action?: React.ReactNode;
  disabled?: boolean;
  /** Tooltip for the whole row when the item is disabled (still not selectable). */
  disabledTooltip?: string;
};
export type SearchSelectProps<T extends Item> = {
  disabled?: boolean;
  label: React.ReactNode;
  items: T[] | Group<T>[];
  selectedItem: T | null;
  onChange: (item: T | null) => void;
  renderItem?: (item: T) => React.ReactNode;
  renderSelectedItem?: (item: T | null) => React.ReactNode;
  renderEmptyItems?: () => React.ReactNode;
  error?: string;
  alert?: React.ReactNode;
  invalidSelectionErrorMessage?: string;
  leftIcon?: React.ReactNode;
  /*
    Some cases the dropdown needs to be open up,
    because it would break the page layout or create a unecessary scroll.
  */
  openDirection?: "up" | "down";
  maxHeight?: string;
  placeholder?: string;
  formControlProps?: FormControlProps;
  formLabelProps?: FormLabelProps;
  onInputChange?: (inputValue: string) => void;
  variant?: Variant;
  hideClearButton?: boolean;
};
/**
 * Custom Select component capable of rendering custom option and search functionality.
 */
export function SearchSelect<T extends Item>(props: SearchSelectProps<T>) {
  const {
    items,
    onChange,
    renderItem,
    renderSelectedItem,
    disabled,
    renderEmptyItems,
    alert,
    openDirection = "down",
    placeholder = "Search...",
    maxHeight = "20rem",
    onInputChange,
    invalidSelectionErrorMessage = "Selected item is not available",
    variant = "locked",
    leftIcon,
    hideClearButton = false,
  } = props;
  let error = props.error;

  const itemToString = React.useCallback((value: T | null) => {
    return value ? value.label : "";
  }, []);

  const [searchValue, setSearchValue] = useState(() =>
    itemToString(props.selectedItem)
  );

  const groups = useMemo(() => toGroupList(items), [items]);
  const allItems = useMemo(() => toItemList(items), [items]);
  const filteredGroups = useMemo(
    () => filterGroups(groups, searchValue),
    [groups, searchValue]
  );
  const filteredItems = useMemo(
    () => filteredGroups.flatMap((group) => group.items),
    [filteredGroups]
  );

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    highlightedIndex,
    selectedItem,
    selectItem,
    closeMenu,
    getToggleButtonProps,
  } = useCombobox<T>({
    items: filteredItems,
    itemToString,
    initialInputValue: itemToString(props.selectedItem),
    selectedItem: props.selectedItem,
    onInputValueChange: ({ inputValue: newInputValue }) => {
      const nextValue = newInputValue ?? "";
      onInputChange?.(nextValue);
      setSearchValue(nextValue);
    },
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      onChange(newSelectedItem || null);
      closeMenu();
    },
    isItemDisabled: (item) => {
      return item.disabled ?? false;
    },
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputClick:
        case useCombobox.stateChangeTypes.ToggleButtonClick:
        case useCombobox.stateChangeTypes.FunctionOpenMenu:
          if (!state.isOpen && changes.isOpen) {
            return { ...changes, inputValue: "" };
          }
          return changes;
        case useCombobox.stateChangeTypes.InputBlur:
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.FunctionCloseMenu:
          if (state.isOpen && !changes.isOpen) {
            return {
              ...changes,
              inputValue: itemToString(changes.selectedItem ?? null),
            };
          }
          return changes;
        default:
          return changes;
      }
    },
  });

  const selectedItemVariant: SelectedItemVariant =
    variant === "locked" && selectedItem !== null ? "locked-selected" : variant;

  const hasInvalidSelection = useMemo(
    () =>
      !!selectedItem &&
      allItems.find((item) => item.key === selectedItem.key) === undefined,
    [allItems, selectedItem]
  );

  error =
    hasInvalidSelection && error === undefined
      ? invalidSelectionErrorMessage
      : error;

  const selectedStyle: FlexProps =
    selectedItemVariant === "locked-selected"
      ? {
          borderColor: "primary.500",
          backgroundColor: "primary.50",
          borderWidth: "2px",
          whiteSpace: "pre-line",
        }
      : {};

  const errorStyle: FlexProps =
    !disabled && error
      ? {
          borderWidth: "1px",
          borderColor: "red.500",
          boxShadow: "0 0 0 1px var(--chakra-colors-red-500)",
        }
      : {};

  const disabledStyle: FlexProps = disabled
    ? {
        cursor: "not-allowed",
        opacity: 0.4,
      }
    : {};

  const focusStyle: FlexProps = disabled
    ? {}
    : {
        outline: "0px",
        border: "1px solid",
        borderColor: "blue.500",
        boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
      };

  const defaultRenderSelectedItem = React.useCallback((item: T | null) => {
    return item?.label ?? "Please select";
  }, []);

  const defaultRenderItem = React.useCallback(
    (item: Item) => (
      <Flex direction="column">
        <Flex gap="2" justifyContent="space-between" alignItems="center">
          <Box whiteSpace="pre-line">
            <HighlightedText text={item.label} keyword={searchValue} invert />
          </Box>
          {item.action ?? null}
        </Flex>
        {item.description ? (
          <Box color="blackAlpha.700" fontSize="sm">
            {item.description}
          </Box>
        ) : null}
      </Flex>
    ),
    [searchValue]
  );

  const listItemsNode = (() => {
    let index = 0;
    return filteredGroups.map((group, groupIndex) => {
      const itemsInGroup = group.items.map((item) => {
        const itemIndex = index;
        index += 1;
        return (
          <ListItem
            key={item.key}
            paddingX={4}
            paddingY={2}
            transition="background 0.2s"
            background={
              highlightedIndex === itemIndex
                ? "gray.200"
                : selectedItem === item
                ? "gray.100"
                : undefined
            }
            {...getItemProps({ item, index: itemIndex })}
            aria-label={item.label}
            _hover={{ background: "gray.200" }}
            cursor={item.disabled ? "not-allowed" : "pointer"}
            opacity={item.disabled ? 0.4 : undefined}
          >
            <ConditionalTooltip
              label={item.disabledTooltip}
              showTooltip={!!item.disabled && !!item.disabledTooltip}
              openDelay={300}
              hasArrow
            >
              <Box width="100%">
                {renderItem ? renderItem(item) : defaultRenderItem(item)}
              </Box>
            </ConditionalTooltip>
          </ListItem>
        );
      });

      return (
        <Fragment key={group.originalIndex}>
          {groupIndex > 0 ? <Divider my={1} /> : null}
          {group.group != null ? (
            <Flex paddingX={4} paddingY={2}>
              <Text fontSize="sm" fontWeight="bold">
                {group.group}
              </Text>
            </Flex>
          ) : null}
          {itemsInGroup}
        </Fragment>
      );
    });
  })();

  return (
    <FormControl
      isInvalid={!disabled && error !== undefined}
      isDisabled={disabled}
      display="flex"
      flexDirection="column"
      gap="2"
      {...props.formControlProps}
    >
      {props.label ? (
        <FormLabel margin="0" {...props.formLabelProps} {...getLabelProps()}>
          {props.label}
        </FormLabel>
      ) : null}
      {alert && <Box> {alert}</Box>}
      <Flex
        borderStyle="solid"
        borderWidth={1}
        borderRadius={6}
        padding={2}
        paddingLeft={4}
        justifyContent="space-between"
        alignItems="center"
        transitionProperty="border-color,box-shadow"
        transitionDuration="0.2s"
        _focus={focusStyle}
        {...selectedStyle}
        {...errorStyle}
        {...disabledStyle}
      >
        <Flex flexGrow="1" gap="2" alignItems="center">
          {selectedItemVariant !== "locked-selected" ? (
            leftIcon ? (
              leftIcon
            ) : (
              <Icon as={RiSearchLine} color="gray.500" />
            )
          ) : null}
          <Box
            display={
              !isOpen && selectedItemVariant === "locked-selected"
                ? undefined
                : "none"
            }
          >
            {renderSelectedItem
              ? renderSelectedItem(selectedItem)
              : defaultRenderSelectedItem(selectedItem)}
          </Box>
          {isOpen || selectedItemVariant !== "locked-selected" ? (
            <Input
              placeholder={placeholder}
              variant="unstyled"
              {...getInputProps()}
              disabled={disabled}
            />
          ) : (
            <VisuallyHiddenInput
              placeholder={placeholder}
              {...getInputProps()}
              disabled={disabled}
            />
          )}
        </Flex>
        <Flex alignItems="center" paddingRight={2} gap="2">
          {!hideClearButton && selectedItem !== null ? (
            <IconButton
              aria-label="Clear selection"
              icon={<RiCloseLine />}
              minWidth={5}
              boxSize={5}
              width={1}
              variant="ghost"
              _hover={{ bg: "none" }}
              onClick={() => {
                selectItem(null);
                setSearchValue("");
              }}
            />
          ) : null}
          {selectedItemVariant === "unlocked" ? (
            <IconButton
              icon={isOpen ? <RiArrowUpSLine /> : <RiArrowDownSLine />}
              aria-label="Open or close dropdown"
              minWidth={5}
              boxSize={5}
              variant="ghost"
              _hover={{ bg: "none" }}
              {...getToggleButtonProps()}
            />
          ) : null}
        </Flex>
      </Flex>
      <List
        {...getMenuProps()}
        paddingY={2}
        borderColor="inherit"
        borderWidth={1}
        borderStyle="solid"
        display={isOpen ? "flex" : "none"}
        position="absolute"
        bg="white"
        width="100%"
        borderRadius={4}
        flexDirection="column"
        boxShadow="lg"
        bottom={
          openDirection === "up" ? (props.label ? "66%" : "100%") : undefined
        }
        top={openDirection === "down" ? "100%" : undefined}
        zIndex={2}
        maxHeight={maxHeight}
        overflowY={maxHeight ? "scroll" : undefined}
      >
        {isOpen && filteredItems.length === 0 && renderEmptyItems
          ? renderEmptyItems()
          : isOpen
          ? listItemsNode
          : null}
      </List>
      {error && <FormErrorMessage margin="0">{error}</FormErrorMessage>}
    </FormControl>
  );
}

type Variant = "locked" | "unlocked";
type SelectedItemVariant = Variant | "locked-selected";
